import { Subject, Subscription, timer, race, of } from "rxjs";
import {
  switchAll,
  catchError,
  tap,
  concatMap,
  delay,
  filter,
  take,
  retryWhen,
} from "rxjs/operators";

import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { getSiteConfigs } from "./site-config";

const WS_SITE = getSiteConfigs().WS_SITE;

export default class WebSocket {
  private socket$: WebSocketSubject<any> | undefined;

  private messagesSubject$ = new Subject() as any;
  public messages$ = this.messagesSubject$.pipe(
    switchAll(),
    catchError((e) => {
      throw e;
    })
  );

  // 指向"当前" socket 的心跳订阅。每个 socket 实例在 getNewWebSocket 里各自持有自己的句柄，
  // 旧连接迟到的 close 事件只会清理自己的心跳，不会误杀新连接的心跳
  private heartbeatSub: Subscription | undefined;

  private token: string | undefined;

  connect(token: string) {
    this.token = token;
    if (!this.socket$ || this.socket$.closed) {
      this.socket$ = this.getNewWebSocket();
      const messages = this.socket$.pipe(
        retryWhen((errors) =>
          errors.pipe(
            tap(() => console.log("retry")),
            delay(20000)
          )
        )
      );
      this.messagesSubject$.next(messages);
    }
  }

  sendMessage(msg: any) {
    this.socket$?.next(msg);
  }

  close() {
    // 同步停掉当前心跳，避免 complete 之后、close 事件到达之前又发一次 H
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = undefined;

    if (!this.socket$ || this.socket$.closed) {
      this.socket$ = undefined;
      return;
    }

    this.socket$.complete();
    this.socket$ = undefined;
  }

  // 心跳超时说明连接已经半开（尤其是从后台恢复后），直接重建连接而不是只停掉心跳
  reconnect() {
    if (!this.token) return;
    console.log("[DataService]: reconnect " + new Date().toLocaleString());
    this.close();
    this.connect(this.token);
  }

  getNewWebSocket() {
    const url = WS_SITE + `?token=${this.token}`;

    // 绑定到本次创建的 socket 实例上的心跳句柄
    let heartbeatSub: Subscription | undefined;

    const stopHeartbeat = () => {
      heartbeatSub?.unsubscribe();
      if (this.heartbeatSub === heartbeatSub) {
        this.heartbeatSub = undefined;
      }
      heartbeatSub = undefined;
    };

    const socket: WebSocketSubject<any> = webSocket({
      url,
      deserializer: (val) => {
        if (val.data === "H") return "H";
        else return JSON.parse(val.data);
      },
      serializer: (val) => {
        if (val === "H") {
          return "H";
        } else return JSON.stringify(val);
      },
      openObserver: {
        next: () => {
          //interval 45s, 等待15s，累计一分钟心跳
          console.log("[DataService]: send H" + new Date().toLocaleString());
          stopHeartbeat();
          heartbeatSub = timer(0, 15000)
            .pipe(
              tap(() => {
                socket.next("H");
              }),
              concatMap(() =>
                race(
                  of("timeout").pipe(delay(15000)),
                  socket.pipe(
                    filter((msg) => msg === "H"),
                    take(1)
                  )
                )
              )
            )
            .subscribe(
              (resp) => {
                if (resp === "timeout") {
                  console.log("[DataService]: timeout");
                  stopHeartbeat();
                  // 只有当这个 socket 仍是当前连接时才重连，避免旧连接的心跳干扰新连接
                  if (this.socket$ === socket) {
                    this.reconnect();
                  }
                }
              },
              () => {
                stopHeartbeat();
              }
            );
          this.heartbeatSub = heartbeatSub;
        },
      },
      closeObserver: {
        next: () => {
          console.log("[DataService]: connection closed");
          stopHeartbeat();
        },
      },
    });

    return socket;
  }
}
